import type { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import type { ReactNode } from 'react';

import { ClearCacheBtn, StorageSize } from '@dvb/ui';
import {
  faFolderOpen,
  faFont,
  faGlobe,
  faMoon,
  faPalette,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn, Switch } from '@heroui/react';
import { TanstackQueryIDBPersister } from '@milestone-portal/common/others/storage/TanstackQueryIDBPersister';
import { useSettingStore } from '@milestone-portal/context/useSettingStore';
import { Container } from '@milestone-portal/ui/Container/Container';
import { DarkModeToggle } from '@milestone-portal/ui/DarkModeToggle/DarkModeToggle';
import { HeaderBar } from '@milestone-portal/ui/HeaderBar/HeaderBar';
import { LanguageMenu } from '@milestone-portal/ui/LanguageMenu/LanguageMenu';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FontScaleMenu } from './-components/FontScaleMenu';

interface NavigationContent {
  description?: ReactNode;
  element: ReactNode;
  icon: IconDefinition;
  title: string;
}

export const Route = createFileRoute('/_authed/setting/')({
  component: SettingIndex,
});

function SettingIndex() {
  const { t } = useTranslation();
  const { hasAnimation, toggleHasAnimation } = useSettingStore();

  const clearCache = useCallback(async () => {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map((cacheName) => caches.delete(cacheName))
      );

      await TanstackQueryIDBPersister.removeClient();

      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((r) => r.unregister()));
      }

      window.location.reload();
      return true;
    } catch (error) {
      return false;
    }
  }, []);

  const content: NavigationContent[] = useMemo(
    () => [
      {
        description: t('disableItCanImprovePerformance', { ns: 'ui' }),
        element: (
          <Switch
            classNames={{ wrapper: cn`mr-0` }}
            isSelected={hasAnimation}
            onChange={toggleHasAnimation}
          />
        ),
        icon: faPalette,
        title: t('animation'),
      },
      {
        description: t('enableToReduceGlare', { ns: 'ui' }),
        element: <DarkModeToggle isSwitchOnly />,
        icon: faMoon,
        title: t('darkMode'),
      },
      {
        description: t('adjustUIOnlyLang', { ns: 'ui' }),
        element: <LanguageMenu />,
        icon: faGlobe,
        title: t('language'),
      },
      {
        description: t('adjustToEnsureA11y', { ns: 'ui' }),
        element: <FontScaleMenu />,
        icon: faFont,
        title: t('fontScale'),
      },
      {
        description: <StorageSize />,
        element: <ClearCacheBtn handler={clearCache} />,
        icon: faFolderOpen,
        title: t('clearCache'),
      },
    ],
    [hasAnimation, t, clearCache, toggleHasAnimation]
  );

  return (
    <>
      <HeaderBar />
      <Container>
        <ul
          /**
           * Sync transition duration for Webkit browsers, you have to declare
           * transition property clearly.
           * @see https://stackoverflow.com/q/22069877/18793431
           */
          className="border-default-200 bg-default-50 truncate rounded-lg border transition-[background-color,border-color]"
        >
          {content.map(({ description, element, icon, title }) => {
            return (
              <li
                className="border-default-300 flex w-full flex-wrap items-center rounded-t-lg border-b p-4 transition-[background-color,border-color]"
                key={title}
              >
                <div className="mr-2">
                  <FontAwesomeIcon
                    className="w-[24px] transition-[font-size]"
                    icon={icon}
                  />
                </div>
                {/*
                    FIXME: Safari Webkit transition issue, I have to declare transition property explicitly for each element,
                    Also I can't set it in title (transition will disappear...)
                 */}
                <div className="flex flex-1 flex-col overflow-hidden transition-[font-size]">
                  <p className="truncate">{title}</p>
                  <p className="text-default-500 line-clamp-2 whitespace-break-spaces break-words text-sm transition-[font-size]">
                    {description}
                  </p>
                </div>
                <div className="flex justify-end">{element}</div>
              </li>
            );
          })}
        </ul>
        <p className="text-default-500 mt-16 text-center">{`${t(
          'version'
        )}: ${APP_VERSION}`}</p>
      </Container>
    </>
  );
}
